import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { RootState } from "../../state/store";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import OperationsTable from "./OperationsTable";
import TablePage from "../../components/Layout/TablePage";
import SaveButton from "../../components/Buttons/SaveButton";
import Button from "../../components/Buttons/Button";
import { useGlobalModal } from "../../components/Contexts/GlobalModalContext";
import MultipleStoppagesModal from "./MultipleStoppagesModal";
import { OptionResponse } from "../../DTOs/Common/OptionResponse";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { useSnackbar } from "notistack";
import { operationsAjax } from "../../ajax/OperationsAjax";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { TripleShift } from "../../types/Shift";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { enqueue, hasQueuedCompletion } from "../../offline/queueOps";
import { queryClient } from "../../queryClient";
import { computeShiftId } from "../../offline/computeShiftId";
import { isOnlineNow } from "../../offline/connectivity";
import { withTimeout } from "../../offline/withTimeout";
import {
  EARLIEST_COMPLETION_MINUTES_BEFORE_START,
  createProvisionalNextShift,
  minutesBeforeNextShiftStart,
} from "../../offline/provisionalShift";

const Operations: React.FC = () => {
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const latestShift = useSelector((state: RootState) => state.shift.latestShift);
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate);
  const provisionalShift = useSelector((state: RootState) => state.shift.provisionalShift);
  const { selectedDate, selectedShift } = useShiftTimes();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)
  const dispatch = useDispatch();
  const { showModal, hideModal } = useGlobalModal();
  const { enqueueSnackbar } = useSnackbar();
  const { data: stoppageTypes = new OptionResponse([]) } = useQuery({
    queryKey: ['references', 'stoppageTypes'],
    queryFn: async () => {
      const res = await stoppagesAjax.getStoppageTypes();
      if (ErrorResponse.isErrorResponse(res)) return new OptionResponse([]);
      return res;
    },
    staleTime: 5 * 60 * 1000,
  });
  const [refreshFn, setRefreshFn] = useState<() => void>(() => () => { });
  const {
    options: locationOptions,
    isLoading: isLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['references', 'locations'],
    () => referencesAjax.getLocations({}),
    x => x,
  )

  const triggerRefresh = useCallback(() => {
    refreshFn?.();
  }, [refreshFn]);

  const setLocalSelectedShift = useCallback((newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  }, [dispatch]);

  const showMultipleStoppagesModal = async () => {
    showModal({
      content: () => (<MultipleStoppagesModal
        stoppageTypeGroupOptions={stoppageTypeGroupOptions}
        selectedDate={selectedDate}
        selectedShift={localSelectedShift}
        closeModal={hideModal}
        refreshPage={triggerRefresh}
      />
      ),
      tailwindMaxWidthClass: 'max-w-2xl'
    });
    return true;
  };

  useEffect(() => {
    const newView = "Operations";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const stoppageTypeGroupOptions = useMemo(() => {
    return stoppageTypes.groups.flatMap(group => group.options.map(o => ({
      value: o.key,
      label: o.label,
      groupColor: group.fullObject.colourHex,
      stoppageType: group.label,
      downtimeTypeId: group.key
    })));
  }, [stoppageTypes.groups]);

  const showOfflineCompletionModal = useCallback((message: string) => {
    showModal({
      content: ({ close }) => (
        <div>
          <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
            No network connection
          </h2>
          <p className="!text-theme-text">{message}</p>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="primary" onClick={close}>OK</Button>
          </div>
        </div>
      ),
      tailwindMaxWidthClass: 'max-w-md',
    })
  }, [showModal])

  const completeShiftOffline = async (): Promise<boolean> => {
    const currentDate = selectedDate.toISODate()!
    // Note: a shift may only be completed offline from the latest shift, because completing it
    // creates the next one locally. "Latest" is the persisted latest tblShift record (never
    // DateTime.now()), or the shift a previous offline completion provisionally created.
    const isLatestShift = (latestShiftDate === currentDate && latestShift === localSelectedShift)
      || (provisionalShiftDate === currentDate && provisionalShift === localSelectedShift)

    if (!isLatestShift) {
      showOfflineCompletionModal('Only the latest shift can be completed while offline, because completing it creates the next shift on this device. Select the latest shift, or complete this one once the connection is back.')
      return false
    }

    // Note: completing offline invents the next shift locally, so hold back a click that is far
    // too early - otherwise a mistaken press hours ahead of the handover creates a shift the crew
    // then enters data against.
    const minutesEarly = minutesBeforeNextShiftStart(currentDate, localSelectedShift)
    if (minutesEarly > EARLIEST_COMPLETION_MINUTES_BEFORE_START) {
      const wait = minutesEarly > 90
        ? `${(minutesEarly / 60).toFixed(1)} hours`
        : `${Math.round(minutesEarly)} minutes`
      showOfflineCompletionModal(`The next shift does not start for another ${wait}, so it cannot be created yet. Complete the shift within ${EARLIEST_COMPLETION_MINUTES_BEFORE_START} minutes of the next shift's start time.`)
      return false
    }

    const shiftId = computeShiftId(currentDate, localSelectedShift)
    let stage = 'reading the offline queue'

    try {
      // Note: queue the completion before the provisional shift's digging patterns. The server
      // applies completions first within a batch, but if the batch is ever rejected and retried
      // item by item, the queue order decides - and a pattern cannot be created before the shift.
      if (!await withTimeout(hasQueuedCompletion(shiftId), stage)) {
        stage = 'queueing the completion'
        await withTimeout(enqueue({
          entity: 'completeShift',
          operation: 'create',
          shiftId,
        }), stage)
      }

      stage = 'creating the new shift on this device'
      const provisional = await withTimeout(createProvisionalNextShift(currentDate, localSelectedShift), stage)
      showOfflineCompletionModal(provisional.operationsCarried > 0
        ? `A provisional shift has been created from cached data - ${provisional.operationsCarried} machines and ${provisional.patternsCarried} digging locations carried over - so you can continue entering data for the next shift immediately. The shift will be completed fully when connectivity is restored: engine hours will be calculated, drilling patterns carried over, and validation run.`
        : 'The shift completion has been queued, but there was no cached data for this shift on this device, so the new shift has no machines yet. It will be filled in as soon as the connection is back. To avoid this, open the Operations list while online before going offline.')
      dispatch(setSelectedDate(provisional.date))
      setLocalSelectedShift(provisional.shift)
      return true;
    } catch (error) {
      // Note: without this the button simply spun on for ever. Every step here is local, so a
      // failure is the browser's own storage refusing to answer - name the step, because nothing
      // else on screen can explain it, and say whether the completion made it into the queue.
      console.error(`Offline shift completion failed while ${stage}`, error)
      const detail = error instanceof Error ? error.message : String(error)
      showOfflineCompletionModal(stage === 'creating the new shift on this device'
        ? `The completion is queued and will be applied when connectivity is restored, but the new shift could not be created on this device (${detail}). Try Complete Shift again; closing and reopening the browser usually clears this.`
        : `The shift could not be completed while ${stage} (${detail}). Nothing has been queued. Try again, and if it keeps happening close and reopen the browser.`)
      return false
    }
  }

  // Note: never decide offline-or-not by predicting connectivity. Reachability is only learned from
  // a request that has already failed, and navigator.onLine reports true for a connection that goes
  // nowhere, so a click in the window before the first failure went to the network, rejected, and
  // the completion was simply lost. Instead: send it, and if the request never reaches the server
  // (AjaxController rejects only when there is no response at all - an HTTP error resolves with
  // errors) fall through to the offline completion. The outcome no longer depends on a guess.
  const completeShift = async () => {
    if (!isOnlineNow()) return completeShiftOffline();

    let res: Awaited<ReturnType<typeof operationsAjax.completeShift>>;
    try {
      res = await operationsAjax.completeShift(selectedDate, localSelectedShift);
    } catch (error) {
      console.error('Complete shift request did not reach the server; completing offline instead', error);
      return completeShiftOffline();
    }

    if (!res.errors && !res.data) {
      enqueueSnackbar("Shift completed successfully.", { variant: 'success', autoHideDuration: 3000 });
      // Note: the date picker asks the server which dates have shifts and holds that answer for 15
      // seconds. Completing a shift creates the next day's, so without this the picker keeps serving
      // the pre-completion answer and the new date drops out of the calendar again moments after
      // appearing.
      await queryClient.invalidateQueries({ queryKey: ['shiftExistence'] });
      return true;
    }
    res.errors?.forEach((error, index) => {
      enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 });
    });
    return false;
  };

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={(date) => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText="Operations List" />
      </Breadcrumbs>
      <div className="flex flex-row gap-1 items-center justify-center shrink-0">
        <SaveButton
          buttonLabel={"Multiple Stoppage"}
          requireConfirmation={false}
          handleSave={showMultipleStoppagesModal}
          disabled={isShiftLocked}
          styleClasses="short:py-1"
        />
        <SaveButton
          buttonLabel={"Complete Shift"}
          requireConfirmation={false}
          handleSave={completeShift}
          disabled={isShiftLocked}
          styleClasses="short:py-1"
        />
      </div>
      <OperationsTable
        selectedShift={localSelectedShift}
        selectedDate={selectedDate}
        stoppageTypeGroupOptions={stoppageTypeGroupOptions}
        locationOptions={locationOptions}
        isLocationOptionsLoading={isLocationOptionsLoading}
        setRefreshFn={setRefreshFn}
      />
    </TablePage>
  );
};

export default Operations;